import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bus, Check, LogIn, LogOut as LogOutIcon, MapPin, ShieldAlert } from 'lucide-react';
import { BUS_LIVE } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { watchLocation } from '@/lib/geolocation';
import { formatTime } from '@/lib/format';

interface TodayRoute {
  id: string;
  name: string;
  vehicle: { name: string; plateNo: string } | null;
  childCount: number;
  trips: { id: string; direction: 'PICKUP' | 'DROPOFF'; startedAt: string | null; endedAt: string | null }[];
}

interface TripBoard {
  trip: {
    id: string;
    routeName: string;
    direction: 'PICKUP' | 'DROPOFF';
    startedAt: string | null;
    endedAt: string | null;
  };
  stops: { id: string; name: string; order: number }[];
  roster: {
    child: { id: string; name: string };
    stopId: string | null;
    boardedAt: string | null;
    alightedAt: string | null;
  }[];
}

const DIRECTION_LABELS = { PICKUP: '등원', DROPOFF: '하원' } as const;

/**
 * 버스 운행 — 명세서 §5.20. 기사·교직원이 승하차를 체크한다.
 * 전원 하차 확인 전에는 종료 버튼이 비활성이다(차내 방치 사고 방지).
 */
export function BusPage() {
  const [tripId, setTripId] = useState('');

  return (
    <Screen withNav={false}>
      <AppBar title="버스 운행" back />
      {tripId ? <TripView tripId={tripId} onBack={() => setTripId('')} /> : <TodayView onOpen={setTripId} />}
    </Screen>
  );
}

function TodayView({ onOpen }: { onOpen: (tripId: string) => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['bus', 'today'],
    queryFn: () => apiData<TodayRoute[]>('/bus/today'),
  });

  const start = useMutation({
    mutationFn: ({ routeId, direction }: { routeId: string; direction: 'PICKUP' | 'DROPOFF' }) =>
      apiData<TripBoard>(`/bus/routes/${routeId}/trips`, { method: 'POST', body: { direction } }),
    onSuccess: (board) => {
      void queryClient.invalidateQueries({ queryKey: ['bus'] });
      onOpen(board.trip.id);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '운행을 시작하지 못했어요.'),
  });

  if (isPending) return <Spinner />;
  if (isError) return <ErrorNote message="노선을 불러오지 못했어요." onRetry={() => void refetch()} />;

  if ((data?.length ?? 0) === 0) {
    return (
      <EmptyState
        title="배정된 노선이 없어요"
        body="원장님이 버스 관리에서 노선과 기사님을 배정하면 여기에 보여요."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {error && <p className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">{error}</p>}

      {data?.map((route) => (
        <Card key={route.id}>
          <div className="mb-2 flex items-center gap-2">
            <Bus size={18} className="text-primary-strong" />
            <p className="font-semibold">{route.name}</p>
            <span className="ml-auto text-sm text-ink-sub">
              {route.vehicle ? `${route.vehicle.name} · ${route.vehicle.plateNo}` : ''} 원아{' '}
              {route.childCount}명
            </span>
          </div>

          <div className="flex gap-2">
            {(['PICKUP', 'DROPOFF'] as const).map((direction) => {
              const trip = route.trips.find((t) => t.direction === direction);
              return (
                <Button
                  key={direction}
                  block
                  variant={trip?.endedAt ? 'outline' : trip ? 'secondary' : 'primary'}
                  loading={start.isPending}
                  onClick={() =>
                    trip ? onOpen(trip.id) : start.mutate({ routeId: route.id, direction })
                  }
                >
                  {DIRECTION_LABELS[direction]}
                  {trip?.endedAt ? ' 완료' : trip ? ' 진행 중' : ' 시작'}
                </Button>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}

function TripView({ tripId, onBack }: { tripId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['bus', 'trip', tripId],
    queryFn: () => apiData<TripBoard>(`/bus/trips/${tripId}`),
    // 운행이 끝나면 더 갱신할 것이 없다
    refetchInterval: (query) => (query.state.data?.trip.endedAt ? false : 20_000),
  });

  const board = useMutation({
    mutationFn: ({ childId, action }: { childId: string; action: 'BOARD' | 'ALIGHT' }) =>
      apiData(`/bus/trips/${tripId}/board`, { method: 'POST', body: { childId, action } }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['bus', 'trip', tripId] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '체크하지 못했어요.'),
  });

  const end = useMutation({
    mutationFn: () => apiData(`/bus/trips/${tripId}/end`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['bus'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '종료하지 못했어요.'),
  });

  if (isPending) return <Spinner />;
  if (isError || !data)
    return <ErrorNote message="운행판을 불러오지 못했어요." onRetry={() => void refetch()} />;

  const onBus = data.roster.filter((row) => row.boardedAt && !row.alightedAt).length;
  const done = Boolean(data.trip.endedAt);

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          ← 노선 목록
        </Button>
        <p className="font-semibold">
          {data.trip.routeName} · {DIRECTION_LABELS[data.trip.direction]}
        </p>
        {done && <Chip tone="primary">운행 완료</Chip>}
      </div>

      {/* 안전 강제 규칙 안내 — 차에 남은 아이 수를 항상 보여 준다 */}
      <p
        className={`flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm ${
          onBus > 0 ? 'bg-accent-soft' : 'bg-primary-soft text-primary-strong'
        }`}
      >
        <ShieldAlert size={15} />
        {onBus > 0 ? `지금 차에 ${onBus}명이 타고 있어요.` : '차에 남은 아이가 없어요.'}
      </p>

      {!done && <LocationShare tripId={tripId} />}

      {error && (
        <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {data.roster.map((row) => (
          <Card key={row.child.id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{row.child.name}</p>
              <p className="text-sm text-ink-sub">
                {row.boardedAt ? `승차 ${formatTime(row.boardedAt)}` : '승차 전'}
                {row.alightedAt ? ` · 하차 ${formatTime(row.alightedAt)}` : ''}
              </p>
            </div>
            {!done && !row.boardedAt && (
              <Button
                size="sm"
                icon={<LogIn size={14} />}
                loading={board.isPending}
                onClick={() => board.mutate({ childId: row.child.id, action: 'BOARD' })}
              >
                승차
              </Button>
            )}
            {!done && row.boardedAt && !row.alightedAt && (
              <Button
                size="sm"
                variant="secondary"
                icon={<LogOutIcon size={14} />}
                loading={board.isPending}
                onClick={() => board.mutate({ childId: row.child.id, action: 'ALIGHT' })}
              >
                하차
              </Button>
            )}
            {row.boardedAt && row.alightedAt && <Check size={18} className="text-primary-strong" />}
          </Card>
        ))}
      </div>

      {!done && (
        <Button
          block
          disabled={onBus > 0}
          loading={end.isPending}
          onClick={() => end.mutate()}
        >
          {onBus > 0 ? `하차 확인 남음 (${onBus}명)` : '운행 종료'}
        </Button>
      )}
    </div>
  );
}

/**
 * 위치 공유 (§5.20 P6) — 기사 단말 GPS를 주기 송출한다.
 * 학부모 지도·정거장 도착 임박 알림이 이 좌표를 쓴다.
 */
function LocationShare({ tripId }: { tripId: string }) {
  const [sharing, setSharing] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!sharing) return;

    // 앱에서는 시스템 위치 권한을 먼저 요청한다 — watchLocation이 플랫폼별로 처리(§5.20).
    let cancelled = false;
    let stop: (() => void) | null = null;
    let lastSentAt = 0;

    void watchLocation(
      (point) => {
        const now = Date.now();
        if (now - lastSentAt < BUS_LIVE.locationIntervalMs) return;
        lastSentAt = now;
        void apiData(`/bus/trips/${tripId}/location`, {
          method: 'POST',
          body: { lat: point.lat, lng: point.lng },
        })
          .then(() => setStatus(`위치 공유 중 · ${formatTime(new Date())} 전송`))
          .catch(() => setStatus('위치 전송이 잠시 끊겼어요. 자동으로 다시 시도해요.'));
      },
      (message) => {
        setStatus(message);
        setSharing(false);
      },
    ).then((cleanup) => {
      if (cancelled) cleanup();
      else stop = cleanup;
    });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [sharing, tripId]);

  return (
    <div className="flex items-center gap-2 rounded-sm bg-surface px-3 py-2 shadow-card">
      <MapPin size={15} className={sharing ? 'text-primary-strong' : 'text-ink-sub'} />
      <p className="min-w-0 flex-1 truncate text-sm text-ink-sub">
        {status ?? '위치를 공유하면 보호자가 버스 위치를 볼 수 있어요.'}
      </p>
      <Button
        size="sm"
        variant={sharing ? 'outline' : 'primary'}
        onClick={() => {
          setSharing((prev) => !prev);
          if (sharing) setStatus(null);
        }}
      >
        {sharing ? '공유 중지' : '위치 공유'}
      </Button>
    </div>
  );
}
